import {
  Popover,
  Stack,
  IconButton,
  CardHeader,
  Card,
  CardContent,
  Divider,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIconOutlined from '@mui/icons-material/EditOutlined';
import DeleteIconOutlined from '@mui/icons-material/DeleteOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import LayersIcon from '@mui/icons-material/Layers';
import moment from 'moment';
import { CalendarEvent } from '@src/types/Events';
import Brightness1Icon from '@mui/icons-material/Brightness1';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Dispatch, SetStateAction } from 'react';

interface EventPopoverProps {
  anchorEl: HTMLElement | null;
  onEdit: () => void;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  activeEvent: CalendarEvent | null;
  currentTZ: string;
  stackName: string;
}

const formatRecurrence = (rrule: CalendarEvent['rrule']): string => {
  if (!rrule) {
    return '';
  }

  const { freq, interval = 1, byweekday, until } = rrule;

  // Format frequency with interval
  let result = interval > 1 ? `Every ${interval} ` : '';
  result += freq.toLowerCase();
  if (interval > 1) {
    result += 's';
  }

  // Add weekday information if present
  if (byweekday && Array.isArray(byweekday) && byweekday.length > 0) {
    const dayMap: { [key: string]: string } = {
      su: 'Sun',
      mo: 'Mon',
      tu: 'Tue',
      we: 'Wed',
      th: 'Thu',
      fr: 'Fri',
      sa: 'Sat',
    };
    const days = byweekday.map((d) => dayMap[d] || d).join(', ');
    result += ` on ${days}`;
  }

  // Add end condition
  if (until) {
    result += ` until ${moment(until).format('MMM DD, YYYY')}`;
  }

  return result.charAt(0).toUpperCase() + result.slice(1);
};

export default function EventPopover({
  anchorEl,
  onEdit,
  onClose,
  setOpen,
  activeEvent,
  currentTZ,
  stackName,
}: EventPopoverProps) {
  const isPopoverOpen = Boolean(anchorEl);
  const id = isPopoverOpen ? 'simple-popover' : undefined;
  const cardSectionSx = {
    p: 1,
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <Popover
      open={isPopoverOpen}
      id={id}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Card
        sx={{
          width: 375,
          p: 0,
        }}
      >
        <CardHeader
          avatar={
            <Brightness1Icon sx={{ color: activeEvent?.backgroundColor }} />
          }
          action={
            <>
              <Tooltip title='Edit event'>
                <IconButton
                  onClick={onEdit}
                  color='primary'
                  aria-label='Edit event'
                >
                  <EditIconOutlined color='action' />
                </IconButton>
              </Tooltip>
              <Tooltip title='Delete event'>
                <IconButton
                  onClick={() => setOpen(true)}
                  color='error'
                  aria-label='Delete event'
                >
                  <DeleteIconOutlined color='action' />
                </IconButton>
              </Tooltip>
            </>
          }
          title={activeEvent?.title}
          sx={{
            p: 1,
            bgcolor: '#4d5256',
          }}
        />
        <CardContent sx={{ p: 0, border: '1px solid #4d5256' }}>
          <Stack>
            <Stack direction='row' spacing={1} sx={cardSectionSx}>
              <Box flex={1}>
                <AccessAlarmIcon sx={{ fontSize: '16px', mr: 1 }} />
                {moment
                  .utc(activeEvent?.start)
                  .tz(currentTZ)
                  .format('DD MMM, YYYY')}
              </Box>
              <Box flex={1}>
                {moment.utc(activeEvent?.start).tz(currentTZ).format('h:mmA')} -{' '}
                {moment.utc(activeEvent?.end).tz(currentTZ).format('h:mmA')}
              </Box>
            </Stack>
            {activeEvent?.rrule && (
              <Stack direction='row' spacing={1} sx={cardSectionSx}>
                <Box flex={1}>
                  <EventRepeatIcon sx={{ fontSize: '16px', mr: 1 }} />
                  <span>Recurrence</span>
                </Box>
                <Box flex={1}>{formatRecurrence(activeEvent.rrule)}</Box>
              </Stack>
            )}
            <Stack direction='row' spacing={1} sx={cardSectionSx}>
              <Box flex={1}>
                <LayersIcon sx={{ fontSize: '16px', mr: 1 }} />
                <span>Stack</span>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  textTransform: 'capitalize',
                }}
              >
                {stackName}
              </Box>
            </Stack>
            <Divider
              sx={{
                my: 1,
                border: '1px solid',
              }}
            />
            <Stack direction='row' spacing={1} sx={cardSectionSx}>
              <Box flex={1}>
                <ArrowForwardIcon sx={{ fontSize: '16px', mr: 1 }} />
                <span>Start Command</span>
              </Box>
              <Tooltip
                title={activeEvent?.startCommands?.join(', ') || 'No commands'}
                placement='top'
              >
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {activeEvent?.startCommands &&
                  activeEvent.startCommands.length > 0 ? (
                    activeEvent.startCommands.join(', ')
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      None
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            </Stack>
            <Stack direction='row' spacing={1} sx={cardSectionSx}>
              <Box flex={1}>
                <ArrowBackIcon sx={{ fontSize: '16px', mr: 1 }} />
                <span>Stop Command</span>
              </Box>
              <Tooltip
                title={activeEvent?.stopCommands?.join(', ') || 'No commands'}
                placement='top'
              >
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {activeEvent?.stopCommands &&
                  activeEvent.stopCommands.length > 0 ? (
                    activeEvent.stopCommands.join(', ')
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      None
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Popover>
  );
}
